// Consolidated Settlement page: Report, Batches, Schedule and Run logs in one tabbed view.
// Tab content is provided by the existing pages rendered with embedded={true}.

import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

// Material Dashboard 2 React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import UnderlineTabs from "components/ui/UnderlineTabs";
import EmptyState from "components/ui/EmptyState";
import usePermission from "components/Utils/usePermission";

// Tab content (existing pages, rendered without their own layout shell)
import CardSettlementSummary from "layouts/reports/CardSettlementSummary";
import SettlementSchedule from "layouts/settlement-schedule";
import SettlementLogs from "layouts/settlement-logs";

// i18n
import { useTranslation } from "react-i18next";

function Settlement() {
  const checkPermission = usePermission();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabs = useMemo(() => {
    const list = [];
    if (checkPermission("Settlement", "Configure")) {
      list.push({ value: "schedule", label: t("settlement_schedule") });
    }
    if (checkPermission("Settlement", "Logs")) {
      list.push({ value: "logs", label: t("settlement_logs") });
    }
    if (checkPermission("Reports", "CardSettlementSummary")) {
      list.push({ value: "report", label: t("settlement_summary") });
    }
    return list;
  }, [checkPermission, t]);

  const requested = searchParams.get("tab");
  const activeTab = tabs.some((tab) => tab.value === requested)
    ? requested
    : tabs.length > 0
    ? tabs[0].value
    : null;

  const handleChange = (event, value) => {
    setSearchParams({ tab: value }, { replace: true });
  };

  // Rendered by whichever tab is active, positioned between its own header
  // row (title/count, filters) and its table/form card.
  const tabsSlot = <UnderlineTabs value={activeTab} onChange={handleChange} tabs={tabs} />;

  return (
    <DashboardLayout>
      <MDBox mt={4}>
        {tabs.length === 0 ? (
          <EmptyState
            icon="lock"
            title={t("no_permission", { defaultValue: "You do not have access to Settlement" })}
          />
        ) : (
          <>
            {/* Only the active tab mounts, so each tab's API calls fire on first activation */}
            {activeTab === "schedule" && <SettlementSchedule embedded tabsSlot={tabsSlot} />}
            {activeTab === "logs" && <SettlementLogs embedded tabsSlot={tabsSlot} />}
            {activeTab === "report" && <CardSettlementSummary embedded tabsSlot={tabsSlot} />}
          </>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Settlement;
